import { v4 as uuid } from 'uuid';
import { getNextActiveStopId } from './get-next-active-stop-id';
import { gradientModelManager } from './gradient-model-manager';
import { interpolateHslColorAtPosition } from './interpolate-hsl-color-at-position';
import { clampPercent } from '../utils/clamp-percent';

const EMPTY_PATCH = {};

export const colorStopsManager = Object.freeze({
  activate(colorStops, stopId) {
    if (!stopId) {
      return EMPTY_PATCH;
    }

    return { activeColorStopId: stopId };
  },

  move(colorStops, stopId, position) {
    if (!stopId || position == null) {
      return EMPTY_PATCH;
    }

    const nextPosition = gradientModelManager.buildLengthPercentage(position);

    const nextStops = colorStops
      .map((stop) => (stop.id === stopId ? { ...stop, length: nextPosition } : stop))
      .sort(
        (a, b) =>
          gradientModelManager.extractStopPosition(a) - gradientModelManager.extractStopPosition(b)
      );

    return {
      activeColorStopId: stopId,
      colorStops: nextStops,
    };
  },

  reorder(colorStops, order) {
    if (!order.length) {
      return EMPTY_PATCH;
    }

    const byId = Object.fromEntries(colorStops.map((stop) => [stop.id, stop]));
    const nextStops = order
      .map((id) => byId[id])
      .filter((stop) => stop != null)
      .sort(
        (a, b) =>
          gradientModelManager.extractStopPosition(a) - gradientModelManager.extractStopPosition(b)
      );

    return { colorStops: nextStops };
  },

  create(colorStops, position) {
    const clampedPosition = clampPercent(Math.round(position));
    const color = interpolateHslColorAtPosition(clampedPosition, colorStops);
    const id = `stop-${uuid()}`;
    const nextStops = [
      ...colorStops,
      {
        id,
        type: 'hsla',
        value: gradientModelManager.buildHslaValue(color),
        length: gradientModelManager.buildLengthPercentage(clampedPosition),
      },
    ].sort(
      (a, b) =>
        gradientModelManager.extractStopPosition(a) - gradientModelManager.extractStopPosition(b)
    );

    return { colorStops: nextStops, activeColorStopId: id };
  },

  remove(colorStops, activeStopId) {
    if (!activeStopId || colorStops.length <= 1) {
      return EMPTY_PATCH;
    }

    const nextActiveStopId = getNextActiveStopId({
      deletedStopId: activeStopId,
      stops: colorStops,
    });

    if (!nextActiveStopId) {
      return EMPTY_PATCH;
    }

    const remainingStops = colorStops.filter((stop) => stop.id !== activeStopId);

    return { colorStops: remainingStops, activeColorStopId: nextActiveStopId };
  },

  mirror(colorStops) {
    const nextStops = colorStops
      .map((stop) => ({
        ...stop,
        length: gradientModelManager.buildLengthPercentage(
          100 - gradientModelManager.extractStopPosition(stop)
        ),
      }))
      .sort(
        (a, b) =>
          gradientModelManager.extractStopPosition(a) - gradientModelManager.extractStopPosition(b)
      );

    return { colorStops: nextStops };
  },

  setPosition(colorStops, id, position) {
    const clampedPosition = clampPercent(position);
    const nextStops = colorStops.map((stop) =>
      stop.id === id
        ? { ...stop, length: gradientModelManager.buildLengthPercentage(clampedPosition) }
        : stop
    );

    return { colorStops: nextStops };
  },
});
